import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import {
  UserCircle,
  EnvelopeSimple,
  Warning,
  PaperPlaneTilt,
  XCircle,
  Eye,
  EyeSlash,
  CheckCircle,
} from "@phosphor-icons/react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  dispatcher: "Dispatcher",
  user: "Viewer",
  customer: "Customer",
  driver: "Driver",
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [resending, setResending] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  if (!user) return null;

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      /* ignore */
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", { name: name.trim(), phone });
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !password) {
      toast.error("New email and current password are required");
      return;
    }
    setSubmittingEmail(true);
    try {
      const { data } = await api.post("/auth/email-change", {
        new_email: newEmail.trim(),
        current_password: password,
      });
      setNewEmail("");
      setPassword("");
      await refreshMe();
      toast.success(data.message || "Verification link sent to your new email");
    } catch (err) {
      await refreshMe(); // pending may exist even if the send failed (Resend hiccup)
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start email change");
    } finally {
      setSubmittingEmail(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/email-change/resend");
      toast.success(`Verification link re-sent to ${data.pending_email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  const cancelEmailChange = async () => {
    setCancelling(true);
    try {
      await api.delete("/auth/email-change");
      await refreshMe();
      toast.success("Email change cancelled — your current email stays active");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl" data-testid="profile-page">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
          Account
        </div>
        <h1
          className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100"
          style={{ fontFamily: "Work Sans" }}
        >
          My Profile
        </h1>
      </div>

      {/* Personal info */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 sm:p-6 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-md bg-[#002BF6] text-white flex items-center justify-center">
            <UserCircle size={15} weight="bold" />
          </div>
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-400">
            Personal information
          </div>
        </div>
        <form onSubmit={saveProfile} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
                Full name
              </Label>
              <Input
                className="mt-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
                Phone
              </Label>
              <Input
                className="mt-1.5"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (876) 555-0123"
                data-testid="profile-phone-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              Role: <b>{ROLE_LABELS[user.role] || user.role}</b>
              {user.company_name ? <> · {user.company_name}</> : null}
            </div>
            <Button
              type="submit"
              disabled={savingProfile}
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              data-testid="profile-save-btn"
            >
              {savingProfile ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>

      {/* Email */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-md bg-zinc-900 dark:bg-zinc-700 text-white flex items-center justify-center">
            <EnvelopeSimple size={15} weight="bold" />
          </div>
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-400">
            Email address
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4" data-testid="profile-current-email">
          <code className="text-sm font-mono text-zinc-900 dark:text-zinc-100">{user.email}</code>
          {user.email_verified && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30">
              <CheckCircle size={11} weight="bold" /> Verified
            </span>
          )}
        </div>

        {user.pending_email ? (
          <div
            className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4"
            data-testid="pending-email-banner"
          >
            <div className="flex items-start gap-3">
              <Warning size={18} weight="fill" className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  Verification pending
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  We sent a confirmation link to{" "}
                  <b className="font-mono" data-testid="pending-email-value">{user.pending_email}</b>. Your current
                  email stays active until you click it. The link expires in 24 hours.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={resendVerification}
                    disabled={resending}
                    className="border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                    data-testid="resend-email-change-btn"
                  >
                    <PaperPlaneTilt size={13} weight="bold" className="mr-1.5" />
                    {resending ? "Sending…" : "Resend link"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={cancelEmailChange}
                    disabled={cancelling}
                    className="text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                    data-testid="cancel-email-change-btn"
                  >
                    <XCircle size={13} weight="bold" className="mr-1.5" />
                    {cancelling ? "Cancelling…" : "Cancel change"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={requestEmailChange} className="space-y-4">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              To change your email, we'll send a verification link to the new address. Your current email keeps
              working until the new one is confirmed.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
                  New email address
                </Label>
                <Input
                  type="email"
                  className="mt-1.5"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="new@example.com"
                  data-testid="new-email-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
                  Current password
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    data-testid="email-change-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    data-testid="toggle-password-visibility"
                    aria-label="Toggle password visibility"
                  >
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={submittingEmail}
                className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
                data-testid="request-email-change-btn"
              >
                <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                {submittingEmail ? "Sending…" : "Send verification link"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
